import { ReactComponent as ChairBedIcon } from 'assets/image/svg/Chair.svg';
import { ReactComponent as FullDoubleBedIcon } from 'assets/image/svg/FullDoubleBed.svg';
import { ReactComponent as DoubleBedIcon } from 'assets/image/svg/DoubleBed.svg';
import { ReactComponent as SingleBedIcon } from 'assets/image/svg/SingleBed.svg';
import { ReactComponent as SofaBedIcon } from 'assets/image/svg/SofaBed.svg';
import { ReactComponent as BunkBedIcon } from 'assets/image/svg/BunkBed.svg';
import { ReactComponent as TwinBedsIcon } from 'assets/image/svg/TwinBeds.svg';
import { FC, SVGProps } from 'react';

export const BED_KEYWORDS = [
   'king size bed',
   'queen size bed',
   'full double',
   'king',
   'queen',
   'chair',
   'double',
   'single',
   'twin',
   'stwin',
   'bunk',
   'sofa',
   'extra bed not included',
   'child bed is not included',
   'child meal not included',
   'handicap accessible',
   'terrace',
   'corner room',
   'welcome drink',
   'aqua park tickets',
   'park access',
   'lounge access',
   'jacuzzi',
];

export const BED_TYPE_MAP: Record<string, string> = {
   double: 'Double',
   'full double': 'Full Double',
   single: 'Single',
   twin: 'Twin',
   stwin: 'Twin',
   sofa: 'Sofa',
   bunk: 'Bunk',
   chair: 'Chair',
   king: 'King',
   'king size bed': 'King',
   queen: 'Queen',
   'queen size bed': 'Queen',
   'extra bed not included': 'extra bed not included',
   'child bed is not included': 'child bed is not included',
   'child meal not included': 'child meal not included',
   'handicap accessible': 'Handicap accessible',
   terrace: 'Terrace view',
   'corner room': 'Corner room',
   'welcome drink': 'welcome drink',
   'aqua park tickets': 'aqua park tickets',
   'park access': 'park access',
   'lounge access': 'lounge access',
   jacuzzi: 'jacuzzi',
};

const SPECIAL_BEDS = [
   'extra bed not included',
   'child bed is not included',
   'child meal not included',
   'handicap accessible',
   'king size bed',
   'queen size bed',
   'terrace',
   'corner room',
   'welcome drink',
   'aqua park tickets',
   'welcome drink',
   'park access',
   'lounge access',
   'jacuzzi',
];

const BED_ICON: Record<string, FC<SVGProps<SVGSVGElement>>> = {
   chair: ChairBedIcon,
   'full double': FullDoubleBedIcon,
   king: FullDoubleBedIcon,
   queen: FullDoubleBedIcon,
   double: DoubleBedIcon,
   single: SingleBedIcon,
   twin: TwinBedsIcon,
   stwin: TwinBedsIcon,
   bunk: BunkBedIcon,
   sofa: SofaBedIcon,
   null: SingleBedIcon,
};

const defaultMiscRoomType = 'Bed Type Not Guaranteed';
const defaultRoomType = 'Minimum 1 Bed';

// ===============================
// Build Bed Display Function
// ===============================
export const buildBedDisplayWithAgodaData = (beddingType: string | null): { name: string; icons: FC<SVGProps<SVGSVGElement>>[] } => {
   // fallback both null
   if (!beddingType) {
      return {
         name: `${defaultRoomType} • ${defaultMiscRoomType}`,
         icons: [BED_ICON['null']],
      };
   }

   const beddingKeys = beddingType?.split('and') || [];
   const convertBeddingType = beddingKeys.map((key: string) => `${capitalize(key.trim())}`).join(' + ');
   const normalizedKeys = beddingKeys.map((key: string) => normalizeBedIconKey(key));
   const icons = normalizedKeys.map((key: string) => BED_ICON[key] ?? BED_ICON['null']);

   // Case: fallback based on beddingType only
   return {
      name: `${convertBeddingType}`,
      icons,
   };
};

// ===============================
// Build Bed Display Function
// ===============================
export const buildBedDisplay = (beddingType: string | null, miscRoomType: string | null): { name: string; icons: FC<SVGProps<SVGSVGElement>>[] } => {
   // fallback both null
   if (!beddingType && !miscRoomType) {
      return {
         name: `${defaultRoomType} • ${defaultMiscRoomType}`,
         icons: [BED_ICON['null']],
      };
   }

   const beddingKeys = beddingType?.split('and') || [];
   const convertBeddingType = beddingKeys.map((key: string) => `${extractBedCount(key.trim())} ${capitalize(key.trim())}`).join(' + ');
   const normalizedKeys = beddingKeys.map((key: string) => normalizeBedIconKey(key));
   const icons = normalizedKeys.map((key: string) => BED_ICON[key] ?? BED_ICON['null']);

   // Case: fallback based on beddingType only
   if (beddingType && !miscRoomType)
      return {
         name: `${convertBeddingType} • ${defaultMiscRoomType}`,
         icons,
      };

   // Extract types from miscRoomType
   const normalBeds = extractBedTypeFromMisc(miscRoomType, SPECIAL_BEDS);

   // Case: miscRoomType has recognized types
   const specialBeds = extractBedTypeFromMisc(miscRoomType).filter(t => SPECIAL_BEDS.includes(t));

   if (!normalBeds.length && specialBeds.length === 1) {
      const icons = [BED_ICON['null']];

      const special = specialBeds[0];
      return { name: !beddingType ? `${defaultRoomType} (${BED_TYPE_MAP[special]})` : `${convertBeddingType} (${BED_TYPE_MAP[special]})`, icons };
   }

   beddingKeys.forEach((key: string) => normalBeds.unshift(capitalize(key ? `${extractBedCount(key)} ${beddingType}` : defaultRoomType)));
   const normalNames = normalBeds.map(t => BED_TYPE_MAP[t] || capitalize(t));
   const specialNames = specialBeds.map(t => BED_TYPE_MAP[t] || capitalize(t));

   const nameParts: string[] = [];
   if (normalNames.length) {
      normalNames[0] = `${normalNames[0]}`;
      nameParts.push(`${normalNames.join(' + ')}`);
   }
   if (specialNames.length) nameParts.push(`(${specialNames.join(', ')})`);

   const normalBedIcons = normalBeds.map(t => BED_ICON[normalizeBedIconKey(t)] ?? BED_ICON['null']);

   return { name: nameParts.join(' '), icons: normalBedIcons };
};

// ===============================
// Helpers
// ===============================

// Normalize text to BED_ICON key
const normalizeBedIconKey = (txt: string | null): string => {
   if (!txt) return 'null';

   const t = txt.toLowerCase();
   if (t.includes('king')) return 'king';
   if (t.includes('queen')) return 'queen';
   if (t.includes('full double')) return 'full double';
   if (t.includes('double')) return 'double';
   if (t.includes('single')) return 'single';
   if (t.includes('twin') || t.includes('stwin')) return 'twin';
   if (t.includes('bunk')) return 'bunk';
   if (t.includes('sofa')) return 'sofa';
   if (t.includes('chair')) return 'chair';

   return 'null';
};

// Extract bed count from string (e.g. "2 Queen Beds" -> 2)
const extractBedCount = (key: string): number | string => {
   const match = key.match(/^(\d+)/);
   const count = match ? parseInt(match[1], 10) : 1;
   return count <= 1 ? count : '';
};

// Extract bed types from miscRoomType
const extractBedTypeFromMisc = (miscRoomType: string | null, excludeTypes?: string[]): string[] => {
   if (!miscRoomType) return [];

   const raw = miscRoomType.toLowerCase();
   const parts = raw.split(',').map(r => r.trim());
   const filterParts = excludeTypes && excludeTypes.length ? parts.filter(r => !excludeTypes?.includes(r)) : parts;
   const found: string[] = [];

   for (const part of filterParts) {
      for (const keyword of BED_KEYWORDS) {
         if (part.includes(keyword) && !found.includes(keyword)) {
            found.push(keyword);
         }
      }
   }

   return found;
};

// Capitalize first character
export const capitalize = (txt: string) => txt.charAt(0).toUpperCase() + txt.slice(1);

export const cleanString = (value: string | number) => {
   if (!value) return '';

   const str = value.toString();
   return str.replace(/«|»/g, '').replace(/_/g, ' ');
};
